/** Atelier Rail is an additive enhancement to existing native collection cards. */
export function atelierRailEnabled(doc: Document = document, location: Pick<Location, 'hostname' | 'pathname'> = window.location): boolean {
  return ['www.raffi-jewellers.ca', 'raffi-jewellers.ca', 'raffi-internal-mod-dev-staging-f924c54ebe58.herokuapp.com'].includes(location.hostname)
    && /^\/(?:fr\/)?collections\/timepieces(?:\/[a-z0-9-]+)*\/?$/i.test(location.pathname)
    && !/rolex|tag[\s_-]*heuer/i.test(location.pathname)
    && !!doc.querySelector('main[data-atelier-rail][data-watch-buying="collection"]');
}

type AtelierRail = {
  track: HTMLElement;
  range: HTMLInputElement;
  wrapper: HTMLElement;
  options: HTMLElement;
  sync: () => void;
  input: () => void;
};
const atelierRails = new Map<HTMLElement, AtelierRail>();
const atelierOptions = new WeakSet<HTMLElement>();
const isolateAtelierOptionEvent = (event: Event): void => event.stopPropagation();
let atelierContainer: HTMLElement | null = null;
let atelierResize: ResizeObserver | null = null;
let atelierRemoval: MutationObserver | null = null;

function removeAtelierRail(state: AtelierRail): void {
  state.track.removeEventListener('scroll', state.sync);
  state.range.removeEventListener('input', state.input);
  atelierResize?.unobserve(state.track);
  state.track.classList.remove('rf-atelier-enhanced');
  state.wrapper.remove();
  atelierRails.delete(state.track);
}

function pruneAtelierRails(): void {
  atelierRails.forEach(state => {
    if (!state.track.isConnected || !state.wrapper.isConnected || !atelierContainer?.contains(state.track)) removeAtelierRail(state);
  });
}

function updateAtelierRail(state: AtelierRail): void {
  const { track, range, wrapper } = state;
  const width = track.clientWidth;
  const extent = Math.max(0, track.scrollWidth - width);
  const links = track.querySelectorAll<HTMLAnchorElement>('.rf-watch-card-options__link');
  const overflowing = Number.isFinite(extent) && width > 0 && extent > 1 && links.length > 1;
  range.max = String(extent);
  range.disabled = !overflowing;
  wrapper.hidden = !overflowing;
  range.value = String(Math.max(0, Math.min(extent, track.scrollLeft)));
  range.style.setProperty('--atelier-thumb-width', `${Math.min(width, Math.max(24, width * width / Math.max(width, track.scrollWidth)))}px`);
  // Only a usable range replaces the native scrollbar. Layout/observer failure
  // therefore leaves the original swipe, wheel and scrollbar behavior intact.
  track.classList.toggle('rf-atelier-enhanced', overflowing);
  state.sync();
}

/** Enhance native related-watch links without selecting a watch or changing cart state. */
export function initAtelierRails(container: HTMLElement): void {
  if (!atelierRailEnabled() || !container.isConnected) return;
  // Keep legacy whole-card swipe and click delegates away from these native
  // links even when the browser falls back to its own horizontal scrollbar.
  // Weak membership and node-owned listeners do not retain removed cards.
  container.querySelectorAll<HTMLElement>('[data-atelier-card] .rf-watch-card-options').forEach(options => {
    if (atelierOptions.has(options)) return;
    options.addEventListener('touchstart', isolateAtelierOptionEvent, { passive: true });
    options.addEventListener('touchend', isolateAtelierOptionEvent, { passive: true });
    options.addEventListener('click', isolateAtelierOptionEvent);
    atelierOptions.add(options);
  });
  if (typeof ResizeObserver !== 'function') return;
  if (atelierContainer !== container) {
    atelierRails.forEach(removeAtelierRail);
    atelierResize?.disconnect();
    atelierRemoval?.disconnect();
    atelierContainer = container;
    try {
      atelierResize = new ResizeObserver(entries => {
        pruneAtelierRails();
        entries.forEach(entry => {
          const state = atelierRails.get(entry.target as HTMLElement);
          if (state) try { updateAtelierRail(state); } catch { removeAtelierRail(state); }
        });
      });
      // Observe only removals inside this result container, never attributes or
      // the page. Loading/empty states can replace cards before the next init.
      if (typeof MutationObserver === 'function') {
        atelierRemoval = new MutationObserver(records => {
          if (records.some(record => record.removedNodes.length > 0)) pruneAtelierRails();
        });
        atelierRemoval.observe(container, { childList: true, subtree: true });
      }
    } catch {
      atelierResize?.disconnect(); atelierRemoval?.disconnect();
      atelierResize = null; atelierRemoval = null; atelierContainer = null;
      return;
    }
  }
  pruneAtelierRails();
  container.querySelectorAll<HTMLElement>('[data-atelier-card] .rf-watch-card-options__track').forEach(track => {
    if (atelierRails.has(track)) return;
    const options = track.closest<HTMLElement>('.rf-watch-card-options');
    if (!options || !track.closest('main[data-atelier-rail][data-watch-buying="collection"]')) return;
    const wrapper = document.createElement('div');
    wrapper.className = 'rf-atelier-rail';
    wrapper.hidden = true;
    const range = document.createElement('input');
    range.type = 'range';
    if (range.type !== 'range') return;
    range.className = 'rf-atelier-range';
    range.min = '0'; range.max = '0'; range.step = '1'; range.value = '0';
    const french = document.documentElement.lang.toLowerCase().startsWith('fr');
    range.setAttribute('aria-label', french ? 'Faire défiler les variantes de montre' : 'Scroll watch options');
    const sync = () => {
      const extent = Math.max(0, track.scrollWidth - track.clientWidth);
      range.value = String(Math.max(0, Math.min(extent, track.scrollLeft)));
      const bounds = track.getBoundingClientRect();
      const links = [...track.querySelectorAll<HTMLAnchorElement>('.rf-watch-card-options__link')];
      const visible = links.map((link, index) => ({ index, bounds: link.getBoundingClientRect() }))
        .filter(item => item.bounds.right > bounds.left + 1 && item.bounds.left < bounds.right - 1);
      const first = visible.length ? visible[0].index + 1 : 0;
      const last = visible.length ? visible[visible.length - 1].index + 1 : 0;
      range.setAttribute('aria-valuetext', french ? `Variantes ${first} à ${last} sur ${links.length}` : `Options ${first}–${last} of ${links.length}`);
    };
    const input = () => {
      track.scrollLeft = Math.max(0, Math.min(Number(range.max), Number(range.value)));
      sync();
    };
    const state: AtelierRail = { track, range, wrapper, options, sync, input };
    try {
      wrapper.append(range);
      options.append(wrapper);
      track.addEventListener('scroll', sync, { passive: true });
      range.addEventListener('input', input);
      atelierRails.set(track, state);
      atelierResize!.observe(track);
      updateAtelierRail(state);
    } catch { removeAtelierRail(state); }
  });
}
